import clsx from "clsx";
import { type ReactNode, useEffect, useRef } from "react";
import { alreadyShown, flash, markShown } from "../lib/flash";

/** The area it scrolls in: a record window's tab (under its Needs attention), else the page. */
function scrollerOf(el: HTMLElement): HTMLElement | null {
    for (let p = el.parentElement; p; p = p.parentElement) {
        const overflow = getComputedStyle(p).overflowY;
        if (overflow === "auto" || overflow === "scroll") return p;
    }
    return null;
}

/**
 * Scroll `el` to the middle of the area it scrolls in (its top, when it's too tall to fit), that area only: not the window or page
 * around it, as scrollIntoView would (centring it on the whole screen, under a window's Needs attention).
 */
function centre(el: HTMLElement) {
    const scroller = scrollerOf(el);
    if (!scroller) {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        return;
    }
    const box = el.getBoundingClientRect();
    const area = scroller.getBoundingClientRect();
    const tall = box.height > area.height * 0.8;
    const offset = box.top - area.top - (tall ? 12 : (area.height - box.height) / 2);
    scroller.scrollTo({ top: scroller.scrollTop + offset, behavior: "smooth" });
}

/**
 * Where a warning at the top of a window is put right (its Show brings you here): the whole warning,
 * with what it needs. Each new `token` (lib/flash.ts nextShowToken) scrolls it into view and flashes it, once: drawn again (back on
 * its tab), it doesn't.
 * `attached`: a strip along the bottom of the card it's about (the card has class fix-card, and
 * flashes with it). Nothing in it: nothing shows.
 *
 * Brought into view with what's around it that belongs to it (a `data-fix-scope` around the card: its heading above it), so
 * that isn't cut off.
 *
 * The standard for a warning about a section being edited: its save bar takes the warning's place (saving or cancelling is how
 * it's put right now), so the save bar is in a FixHere given the same token, and Show brings you to the section and flashes it.
 */
export function FixHere({ token, attached, className, children }: Readonly<{ token?: number; attached?: boolean; className?: string; children: ReactNode }>) {
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const el = ref.current;
        if (token === undefined || !el || alreadyShown(token)) return;
        // What flashes in the middle of the screen: attached, the whole card it's along the bottom of (not just its strip); too tall
        // to fit, its top.
        const shown = (attached ? el.closest<HTMLElement>(".fix-card") : null) ?? el;
        flash(el, "is-shown");
        // Centred once the page has laid out (two frames): Show from another tab draws this one first, and centring before the
        // rest of it is there scrolls somewhere else (pressing Show again, already there, would then move it).
        let frame = requestAnimationFrame(() => {
            frame = requestAnimationFrame(() => {
                centre(shown.closest<HTMLElement>("[data-fix-scope]") ?? shown);
                markShown(token);
            });
        });
        return () => cancelAnimationFrame(frame);
    }, [token, attached]);
    return (
        <div ref={ref} className={clsx("fix-here empty:hidden", attached && "is-attached", className)}>
            {children}
        </div>
    );
}
